import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
    Globe, Plus, Search, MoreHorizontal, Copy, Archive, Trash2,
    FileText, Layers, Target, Loader2, ArchiveRestore
} from 'lucide-react';
import TemplatePicker from '../components/sites/TemplatePicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SITE_TYPES = [
    { value: 'website', label: 'Website', icon: Globe },
    { value: 'landing_page', label: 'Landing Page', icon: FileText },
    { value: 'funnel', label: 'Funnel', icon: Target },
];

const TYPE_ICONS = {
    website: Globe,
    landing_page: FileText,
    funnel: Target,
};

const STATUS_STYLES = {
    draft: 'bg-amber-50 text-amber-700',
    published: 'bg-emerald-50 text-emerald-700',
    archived: 'bg-stone-100 text-stone-500',
};

function SiteCard({ site, onEdit, onDuplicate, onArchiveToggle, onDelete }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const TypeIcon = TYPE_ICONS[site.site_type] || Globe;

    useEffect(() => {
        function handleClickOutside(e) {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        }
        if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen]);

    const typeLabel = SITE_TYPES.find(t => t.value === site.site_type)?.label || site.site_type;
    const statusLabel = site.status?.charAt(0).toUpperCase() + site.status?.slice(1);

    return (
        // NOTE: do NOT add `overflow-hidden` here — it clips the dropdown
        // menu below. Rounded top corners on the thumbnail are handled
        // with `rounded-t-2xl` instead.
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm hover:shadow-md transition-shadow">
            {/* Thumbnail */}
            <div
                className="h-36 flex items-center justify-center cursor-pointer rounded-t-2xl"
                style={{ background: 'linear-gradient(135deg, #e7e5e4 0%, #d6d3d1 50%, #a8a29e 100%)' }}
                onClick={() => onEdit(site)}
            >
                <Globe className="w-12 h-12 text-stone-400/60" />
            </div>

            <div className="p-5">
                {/* Name + Status */}
                <div className="flex items-start justify-between mb-2">
                    <h3
                        className="font-semibold text-stone-900 truncate flex-1 mr-2 cursor-pointer hover:text-stone-700"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                        onClick={() => onEdit(site)}
                    >
                        {site.name}
                    </h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS_STYLES[site.status] || STATUS_STYLES.draft}`}>
                        {statusLabel}
                    </span>
                </div>

                {/* Type badge */}
                <div className="flex items-center gap-1.5 mb-3">
                    <TypeIcon className="w-3.5 h-3.5 text-stone-400" />
                    <span className="text-xs font-medium text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full">
                        {typeLabel}
                    </span>
                </div>

                {/* Meta */}
                <div className="flex items-center gap-3 text-xs text-stone-400 mb-4">
                    <span className="flex items-center gap-1">
                        <Layers className="w-3 h-3" />
                        {site.page_count ?? 0} pages
                    </span>
                    {site.updated_at && (
                        <span>
                            Updated {formatDistanceToNow(new Date(site.updated_at), { addSuffix: true })}
                        </span>
                    )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        className="bg-stone-900 hover:bg-stone-800 text-white flex-1"
                        onClick={() => onEdit(site)}
                    >
                        Edit
                    </Button>
                    <div className="relative" ref={menuRef}>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setMenuOpen(!menuOpen)}
                            className="px-2"
                        >
                            <MoreHorizontal className="w-4 h-4" />
                        </Button>
                        {menuOpen && (
                            <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl border border-stone-200 shadow-lg z-50 py-1"
                                data-testid="site-card-menu"
                            >
                                <button
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
                                    onClick={() => { setMenuOpen(false); onDuplicate(site); }}
                                >
                                    <Copy className="w-4 h-4 text-stone-400" />
                                    Duplicate
                                </button>
                                <button
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
                                    onClick={() => { setMenuOpen(false); onArchiveToggle(site); }}
                                >
                                    {site.status === 'archived' ? (
                                        <>
                                            <ArchiveRestore className="w-4 h-4 text-stone-400" />
                                            Unarchive
                                        </>
                                    ) : (
                                        <>
                                            <Archive className="w-4 h-4 text-stone-400" />
                                            Archive
                                        </>
                                    )}
                                </button>
                                <button
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                                    onClick={() => { setMenuOpen(false); onDelete(site); }}
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function SkeletonCard() {
    return (
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden animate-pulse">
            <div className="h-36 bg-stone-100" />
            <div className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="h-4 bg-stone-100 rounded w-2/3" />
                    <div className="h-5 bg-stone-100 rounded-full w-16" />
                </div>
                <div className="h-5 bg-stone-100 rounded-full w-24" />
                <div className="flex gap-3">
                    <div className="h-3 bg-stone-100 rounded w-16" />
                    <div className="h-3 bg-stone-100 rounded w-24" />
                </div>
                <div className="flex gap-2">
                    <div className="h-8 bg-stone-100 rounded flex-1" />
                    <div className="h-8 bg-stone-100 rounded w-10" />
                </div>
            </div>
        </div>
    );
}

export default function Sites() {
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [createName, setCreateName] = useState('');
    const [createType, setCreateType] = useState('website');
    const [creating, setCreating] = useState(false);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);
    const [pendingSiteId, setPendingSiteId] = useState(null);

    const loadSites = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/sites`, { headers: getAuthHeader() });
            setSites(Array.isArray(res.data) ? res.data : res.data?.data || []);
        } catch {
            toast.error('Failed to load sites');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    useEffect(() => { loadSites(); }, [loadSites]);

    const handleCreate = async () => {
        if (!createName.trim()) { toast.error('Site name is required'); return; }
        setCreating(true);
        try {
            const res = await axios.post(`${API}/sites`, { name: createName, site_type: createType }, { headers: getAuthHeader() });
            toast.success('Site created');
            setShowCreate(false);
            setCreateName('');
            setCreateType('website');
            // Show template picker before navigating to builder
            setPendingSiteId(res.data.site_id);
            setShowTemplatePicker(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create site');
        } finally {
            setCreating(false);
        }
    };

    const handleTemplateSelect = async (template) => {
        const siteId = pendingSiteId;
        setShowTemplatePicker(false);

        if (!siteId) return;

        try {
            // Create the first page with the template's blocks
            const blocks = template.blocks || [];
            await axios.post(
                `${API}/sites/${siteId}/pages`,
                {
                    name: 'Home',
                    slug: 'home',
                    is_homepage: true,
                    blocks,
                },
                { headers: getAuthHeader() }
            );
        } catch {
            // Non-fatal — the page may fail but the site is already created
            toast.error('Could not apply template, but your site was created');
        }

        setPendingSiteId(null);
        navigate(`/sites/${siteId}/edit`);
    };

    const handleTemplatePickerClose = () => {
        // If they close without picking, just navigate to the blank site
        const siteId = pendingSiteId;
        setShowTemplatePicker(false);
        setPendingSiteId(null);
        if (siteId) navigate(`/sites/${siteId}/edit`);
    };

    const handleEdit = (site) => {
        navigate(`/sites/${site.site_id}/edit`);
    };

    const handleDuplicate = async (site) => {
        try {
            await axios.post(`${API}/sites/${site.site_id}/duplicate`, {}, { headers: getAuthHeader() });
            toast.success(`"${site.name}" duplicated`);
            loadSites();
        } catch {
            toast.error('Failed to duplicate site');
        }
    };

    const handleArchiveToggle = async (site) => {
        try {
            if (site.status === 'archived') {
                await axios.put(`${API}/sites/${site.site_id}`, { status: 'draft' }, { headers: getAuthHeader() });
                toast.success(`"${site.name}" unarchived`);
            } else {
                await axios.delete(`${API}/sites/${site.site_id}`, { headers: getAuthHeader() });
                toast.success(`"${site.name}" archived`);
            }
            loadSites();
        } catch {
            toast.error('Failed to update site');
        }
    };

    const handleDelete = async (site) => {
        if (!window.confirm(`Delete "${site.name}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/sites/${site.site_id}`, { headers: getAuthHeader() });
            toast.success(`"${site.name}" deleted`);
            loadSites();
        } catch {
            toast.error('Failed to delete site');
        }
    };

    const filtered = sites.filter(s => s.name?.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="min-h-screen p-6" style={{ backgroundColor: '#FAFAF5' }}>
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Sites
                    </h1>
                    <p className="text-sm text-stone-500 mt-1">
                        Build and publish websites, landing pages, and funnels
                    </p>
                </div>
                <Button onClick={() => setShowCreate(true)} className="bg-stone-900 hover:bg-stone-800">
                    <Plus className="w-4 h-4 mr-1.5" />
                    Create Site
                </Button>
            </div>

            {/* Search */}
            <div className="relative max-w-sm mb-6">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search sites..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                />
            </div>

            {/* Content */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
                </div>
            ) : filtered.length === 0 && !search ? (
                /* Empty state */
                <div className="text-center py-20 bg-white rounded-2xl border border-stone-200">
                    <Globe className="w-14 h-14 mx-auto text-stone-200 mb-4" />
                    <h3 className="text-lg font-semibold text-stone-700 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        No sites yet
                    </h3>
                    <p className="text-stone-500 text-sm mb-6">Create your first website or landing page</p>
                    <Button onClick={() => setShowCreate(true)} className="bg-stone-900 hover:bg-stone-800">
                        <Plus className="w-4 h-4 mr-1.5" />
                        Create Site
                    </Button>
                </div>
            ) : filtered.length === 0 && search ? (
                <div className="text-center py-16">
                    <Search className="w-10 h-10 mx-auto text-stone-200 mb-3" />
                    <p className="text-stone-400 text-sm">No sites matching "{search}"</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(site => (
                        <SiteCard
                            key={site.site_id}
                            site={site}
                            onEdit={handleEdit}
                            onDuplicate={handleDuplicate}
                            onArchiveToggle={handleArchiveToggle}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {/* Create Modal */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create Site</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Site Name</label>
                            <input
                                value={createName}
                                onChange={e => setCreateName(e.target.value)}
                                placeholder="e.g. Product Launch Page"
                                autoFocus
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1.5">Site Type</label>
                            <div className="flex gap-2">
                                {SITE_TYPES.map(type => {
                                    const Icon = type.icon;
                                    const isSelected = createType === type.value;
                                    return (
                                        <button
                                            key={type.value}
                                            onClick={() => setCreateType(type.value)}
                                            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                                                isSelected
                                                    ? 'bg-stone-900 text-white'
                                                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                            }`}
                                        >
                                            <Icon className="w-3.5 h-3.5" />
                                            {type.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                        <Button onClick={handleCreate} disabled={creating} className="bg-stone-900 hover:bg-stone-800">
                            {creating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating...</> : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Template Picker */}
            <TemplatePicker
                isOpen={showTemplatePicker}
                onClose={handleTemplatePickerClose}
                onSelect={handleTemplateSelect}
                getAuthHeader={getAuthHeader}
            />
        </div>
    );
}
